<script setup lang="ts">
  defineOptions({ name: 'CPInput', inheritAttrs: false })

  // Vue core imports
  import type { PropType } from 'vue'

  import { computed, useAttrs } from 'vue'

  // External library imports
  import { HLInput } from '@platform-ui/highrise'

  import type { InputType } from '@platform-ui/highrise'

  // Props definitions
  const props = defineProps({
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    id: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: false,
      default: '',
    },
    modelValue: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    type: {
      type: String as PropType<InputType>,
      required: false,
      default: 'text',
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    maxlength: {
      type: Number,
      required: false,
      default: undefined,
    },
    showCount: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String as PropType<'3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg'>,
      required: false,
      default: 'md',
    },
    surface: {
      type: String as PropType<'default' | 'sidebar'>,
      required: false,
      default: 'default',
    },
  })

  const attrs = useAttrs()

  const inputClass = computed(() => {
    const classes = [attrs.class]
    if (props.surface === 'sidebar') {
      classes.push('cp-input--surface-sidebar')
    }
    return classes
  })

  // Emits definitions
  const emit = defineEmits<{
    'update:modelValue': [value: string]
  }>()

  // User-defined functions
  const handleInput = (value: string | [string, string] | number | null) => {
    emit('update:modelValue', Array.isArray(value) ? value.join('') : String(value ?? ''))
  }
</script>

<template>
  <HLInput
    v-bind="attrs"
    :class="inputClass"
    :id="props.id"
    :size="props.size"
    :disabled="props.disabled"
    :value="props.modelValue ?? props.value"
    :model-value="props.modelValue ?? props.value"
    :placeholder="props.placeholder"
    :type="props.type"
    :readonly="props.readonly"
    :maxlength="props.maxlength"
    :show-count="props.showCount"
    :style="{
      '--n-border-radius': '4px',
    }"
    @update:model-value="handleInput"
  >
    <template v-if="$slots.prefix" #prefix>
      <slot name="prefix" />
    </template>
    <template v-if="$slots.suffix" #suffix>
      <slot name="suffix" />
    </template>
  </HLInput>
</template>

<!--
  HighRise input overrides — unscoped because HighRise sets
  --n-text-color on the wrapper but also injects `color` directly on
  inner element nodes (<input>, <textarea>, count/suffix spans). When
  it does, the CSS-variable chain from a scoped wrapper cannot reach
  those nodes, so the user's font-color token gets ignored. Targeting
  the global .hr-input descendants directly keeps the chain consistent.
  HighRise injects --n-* custom properties as inline styles via its JS
  theme engine, so !important is required to beat inline specificity.
-->
<style>
  /* Background, text, border, caret for the input wrapper */
  .hr-input {
    --n-color: var(--cp-background-500) !important;
    --n-color-focus: var(--cp-background-500) !important;
    /* Disabled: subtle background — 40% of heavy token blended with normal bg ≈ #f5f5f5 */
    --n-color-disabled: color-mix(in srgb, var(--cp-background-600) 40%, var(--cp-background-500)) !important;
    /* Use the user's explicit font-color token so white/dark themes apply correctly */
    --n-text-color: var(--cp-brand-font) !important;
    --n-text-color-disabled: var(--cp-font-500) !important;
    --n-placeholder-color: var(--cp-font-500) !important;
    --n-count-text-color: var(--cp-font-500) !important;
    --n-border: 1px solid var(--cp-border-500) !important;
    --n-border-hover: 1px solid var(--cp-primary-500) !important;
    --n-border-focus: 1px solid var(--cp-primary-500) !important;
    --n-border-disabled: 1px solid var(--cp-border-500) !important;
    --n-caret-color: var(--cp-primary-500) !important;
  }

  /* FOCUS-RING
   * HighRise's shared input theme hard-codes the focus-ring glow to `#D1E0FF`
   * (see highrise481.mjs → commonInputStyles.boxShadowFocus). HighRise emits
   * that value as an inline --n-box-shadow-focus on every input-like component
   * so the focus ring stays blue regardless of the active brand. Override it
   * with --cp-primary-100 so the glow always matches the primary colour. */
  .hr-input {
    --n-box-shadow-focus: 0px 0px 0px 4px var(--cp-primary-100), var(--shadow-xs) !important;
  }

  /* DIRECT TEXT + SURFACE COLOR
   * HighRise injects `color: var(--gray-900)` on .hr-input__input-el /
   * .hr-input__textarea-el (and can leave the wrapper on a white --n-color
   * during error/focus). Target those nodes directly so dark mode keeps
   * both font and background on CP tokens. */
  .hr-input {
    background-color: var(--cp-background-500) !important;
  }

  .hr-input input,
  .hr-input textarea,
  .hr-input .hr-input__input-el,
  .hr-input .hr-input__textarea-el {
    color: var(--cp-brand-font) !important;
    -webkit-text-fill-color: var(--cp-brand-font) !important;
    background-color: transparent !important;
  }

  /* Browser/password-manager autofill paints the native input independently
     of the HighRise wrapper. Cover that paint so autofilled and typed values
     use the same themed surface and text colour. */
  .hr-input input:-webkit-autofill,
  .hr-input input:-webkit-autofill:hover,
  .hr-input input:-webkit-autofill:focus,
  .hr-input input:-webkit-autofill:active,
  .hr-input input:autofill {
    -webkit-box-shadow: 0 0 0 1000px var(--cp-background-500) inset !important;
    box-shadow: 0 0 0 1000px var(--cp-background-500) inset !important;
    color: var(--cp-brand-font) !important;
    -webkit-text-fill-color: var(--cp-brand-font) !important;
    caret-color: var(--cp-primary-500) !important;
  }

  /* Disabled text color — cover both hr-* and n-* class variants HighRise emits */
  .hr-input.hr-input--disabled input,
  .hr-input.n-input--disabled input,
  .hr-input.hr-input--disabled textarea,
  .hr-input.n-input--disabled textarea,
  .hr-input input:disabled,
  .hr-input textarea:disabled {
    color: var(--cp-font-500) !important;
    -webkit-text-fill-color: var(--cp-font-500) !important;
  }

  .hr-input input::placeholder,
  .hr-input textarea::placeholder {
    color: var(--cp-font-500) !important;
    -webkit-text-fill-color: var(--cp-font-500) !important;
    opacity: 1 !important; /* Firefox resets opacity on ::placeholder */
  }

  /* Count / suffix / prefix text nodes inside the input wrapper */
  .hr-input .hr-input__count,
  .hr-input .n-input__count,
  .hr-input .hr-input__suffix,
  .hr-input .hr-input__prefix {
    color: var(--cp-font-500) !important;
  }

  .hr-input textarea {
    color-scheme: inherit;
  }

  /* Placeholder overlay rendered by Naive UI — keep muted.
     Double-class specificity beats the legacy grouped rule that included
     .hr-input__placeholder with --cp-brand-font. */
  .hr-input.hr-input .hr-input__placeholder {
    color: var(--n-placeholder-color) !important;
  }

  /* Disabled state — same background as active; muted text is the sole disabled signal */
  .hr-input .hr-input__input-el[disabled] {
    background-color: color-mix(in srgb, var(--cp-background-600) 40%, var(--cp-background-500)) !important;
  }

  .hr-input[disabled] {
    --n-color-disabled: color-mix(in srgb, var(--cp-background-600) 40%, var(--cp-background-500)) !important;
  }

  /* Top-bar / sidebar chrome — match nav surface instead of page background. */
  .hr-input.cp-input--surface-sidebar {
    --n-color: var(--cp-sidebar-bg) !important;
    --n-color-focus: var(--cp-sidebar-bg) !important;
    --n-text-color: var(--cp-text-on-sidebar) !important;
    --n-placeholder-color: color-mix(in srgb, var(--cp-text-on-sidebar) 60%, transparent) !important;
    --n-count-text-color: color-mix(in srgb, var(--cp-text-on-sidebar) 60%, transparent) !important;
    --n-border: 1px solid var(--cp-sidebar-border) !important;
    --n-border-hover: 1px solid var(--cp-sidebar-border) !important;
    --n-border-focus: 1px solid var(--cp-primary-500) !important;
    --n-caret-color: var(--cp-text-on-sidebar) !important;
  }

  .hr-input.cp-input--surface-sidebar,
  .hr-input.cp-input--surface-sidebar .hr-input-wrapper {
    background-color: var(--cp-sidebar-bg) !important;
  }

  .hr-input.cp-input--surface-sidebar input,
  .hr-input.cp-input--surface-sidebar textarea,
  .hr-input.cp-input--surface-sidebar .hr-input__input-el,
  .hr-input.cp-input--surface-sidebar .hr-input__textarea-el {
    color: var(--cp-text-on-sidebar) !important;
    -webkit-text-fill-color: var(--cp-text-on-sidebar) !important;
  }

  .hr-input.cp-input--surface-sidebar input:-webkit-autofill,
  .hr-input.cp-input--surface-sidebar input:-webkit-autofill:hover,
  .hr-input.cp-input--surface-sidebar input:-webkit-autofill:focus,
  .hr-input.cp-input--surface-sidebar input:-webkit-autofill:active,
  .hr-input.cp-input--surface-sidebar input:autofill {
    -webkit-box-shadow: 0 0 0 1000px var(--cp-sidebar-bg) inset !important;
    box-shadow: 0 0 0 1000px var(--cp-sidebar-bg) inset !important;
    color: var(--cp-text-on-sidebar) !important;
    -webkit-text-fill-color: var(--cp-text-on-sidebar) !important;
    caret-color: var(--cp-text-on-sidebar) !important;
  }

  .hr-input.cp-input--surface-sidebar input::placeholder,
  .hr-input.cp-input--surface-sidebar textarea::placeholder,
  .hr-input.cp-input--surface-sidebar .hr-input__placeholder {
    color: color-mix(in srgb, var(--cp-text-on-sidebar) 60%, transparent) !important;
    -webkit-text-fill-color: color-mix(in srgb, var(--cp-text-on-sidebar) 60%, transparent) !important;
  }

  .hr-input.cp-input--surface-sidebar .hr-input__count,
  .hr-input.cp-input--surface-sidebar .n-input__count,
  .hr-input.cp-input--surface-sidebar .hr-input__suffix,
  .hr-input.cp-input--surface-sidebar .hr-input__prefix {
    color: var(--cp-text-on-sidebar) !important;
  }
</style>
